if (!customElements.get('taffstyle-exit-offer')) {
  customElements.define('taffstyle-exit-offer', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.events = new AbortController();
      this.options = { signal: this.events.signal };
      this.shown = false;
      this.started = performance.now();
      this.hiddenAt = document.hidden ? this.started : null;
      this.hiddenDuration = 0;
      this.status = this.querySelector('.tsro-status');
      this.previewButton = this.querySelector('[data-offer-preview]');
      this.editor = Boolean(window.Shopify?.designMode);
      this.preview = this.editor || (this.dataset.previewAllowed === 'true' && new URL(location.href).searchParams.get('ts_rabatt') === 'preview');
      this.key = 'taffstyle.offer.v1.' + this.dataset.themeId + '.' + this.dataset.code;
      this.delay = Math.max(5, Number(this.dataset.delay) || 30) * 1000;
      this.period = (Number(this.dataset.days) || 30) * 86400000;
      this.excluded = (this.dataset.excluded || '').split(/\r?\n/).map(path => path.trim().replace(/\/$/, '')).filter(Boolean).some(path => location.pathname === path || location.pathname.startsWith(path + '/'));
      this.querySelector('[data-offer-apply]').href = this.discountUrl();
      this.querySelectorAll('[data-offer-close]').forEach(button => button.addEventListener('click', () => this.close(), this.options));
      this.querySelector('[data-offer-copy]').addEventListener('click', () => this.copy(), this.options);
      this.querySelector('[data-offer-apply]').addEventListener('click', event => {
        if (this.editor) { event.preventDefault(); this.status.textContent = this.dataset.editorMessage; }
      }, this.options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, this.options);
      this.dialog.addEventListener('close', () => {
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
        this.returnFocus = null;
      }, this.options);
      this.previewButton.hidden = !this.preview;
      this.previewButton.addEventListener('click', () => this.show(true), this.options);
      document.addEventListener('taffstyle:privacy-state', () => this.privacyChanged(), this.options);
      document.addEventListener('visitorConsentCollected', () => this.privacyChanged(), this.options);
      document.addEventListener('visibilitychange', () => this.visibilityChanged(), this.options);
      window.addEventListener('pagehide', () => { this.close(); clearTimeout(this.idleTimer); }, this.options);
      window.addEventListener('pageshow', () => this.armIdle(), this.options);
      window.addEventListener('resize', () => this.position(), this.options);
      window.visualViewport?.addEventListener('resize', () => this.position(), this.options);
      this.position();
      if (this.preview) {
        this.previewPending = !this.editor;
        this.previewTimer = setTimeout(() => this.tryPreview(), 500);
        return;
      }
      document.documentElement.addEventListener('mouseout', event => {
        if (this.dataset.desktop !== 'true' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        if (event.relatedTarget === null && event.clientY <= 4) this.show();
      }, this.options);
      this.lastY = Math.max(0, window.scrollY);
      this.lastScroll = performance.now();
      this.upDistance = 0;
      window.addEventListener('scroll', () => this.onScroll(), { ...this.options, passive: true });
      ['pointerdown', 'keydown', 'pointermove'].forEach(name => document.addEventListener(name, () => this.armIdle(), { ...this.options, passive: true }));
      this.armIdle();
    }
    discountUrl() {
      const destination = new URL(location.href);
      destination.searchParams.delete('ts_rabatt');
      if (this.dataset.previewAllowed === 'true') destination.searchParams.set('preview_theme_id', this.dataset.themeId);
      const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      return root + 'discount/' + encodeURIComponent(this.dataset.code) + '?redirect=' + encodeURIComponent(destination.pathname + destination.search + destination.hash);
    }
    consentAllowsStorage() {
      try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true; } catch { return false; }
    }
    blocked() {
      if (document.hidden) return true;
      const privacy = document.querySelector('taffstyle-privacy');
      if (privacy && (privacy.dataset.ready !== 'true' || privacy.dataset.decided !== 'true' || privacy.dataset.open === 'true')) return true;
      if (document.querySelector('#cart.toggle, #cart[aria-hidden="false"]')) return true;
      if (Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => element !== this.dialog && element.getClientRects().length)) return true;
      const active = document.activeElement;
      return Boolean(active?.matches('input, textarea, select, [contenteditable="true"]'));
    }
    elapsed() {
      return performance.now() - this.started - this.hiddenDuration - (this.hiddenAt === null ? 0 : performance.now() - this.hiddenAt);
    }
    eligible() {
      if (this.shown || this.excluded || this.blocked() || this.elapsed() < this.delay || !this.consentAllowsStorage()) return false;
      try {
        const last = Number(localStorage.getItem(this.key));
        return !Number.isFinite(last) || last <= 0 || Date.now() - last >= this.period;
      } catch { return false; }
    }
    offerCovered(cart) {
      const offerRate = Number(this.dataset.percent);
      const offerCode = this.dataset.code.trim().toUpperCase();
      const tierRates = {
        '10 % Rabatt': 10,
        '13 % Rabatt': 13,
        '15 % Rabatt': 15
      };
      return (cart.cart_level_discount_applications || []).some(application => {
        if (!(Number(application.total_allocated_amount) > 0)) return false;
        if (String(application.title).trim().toUpperCase() === offerCode) return true;
        const percentage = application.value_type === 'percentage' && application.target_selection === 'all' ? Number(application.value) : 0;
        const tierRate = !application.type || application.type === 'automatic' ? tierRates[application.title] || 0 : 0;
        return Math.max(percentage, tierRate) >= offerRate;
      });
    }
    async checkOffer() {
      if (this.checkingOffer || !this.eligible()) return false;
      this.checkingOffer = true;
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
          signal: AbortSignal.timeout(5000)
        });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (!Array.isArray(cart.cart_level_discount_applications)) throw new Error('Cart discounts unavailable');
        const covered = this.offerCovered(cart);
        this.dataset.discountState = covered ? 'covered' : 'eligible';
        if (covered || !this.isConnected || !this.eligible()) return false;
        return this.openOffer(false);
      } catch {
        this.dataset.discountState = 'unavailable';
        return false;
      } finally {
        this.checkingOffer = false;
      }
    }
    show(forced = false) {
      if (forced) return this.openOffer(true);
      void this.checkOffer();
      return false;
    }
    openOffer(forced = false) {
      if (!this.isConnected || this.dialog.open || (forced ? (!this.preview || this.blocked()) : !this.eligible())) return false;
      if (!forced) {
        try { localStorage.setItem(this.key, String(Date.now())); } catch { return false; }
      }
      this.returnFocus = document.activeElement;
      this.position();
      this.status.textContent = '';
      this.dialog.showModal();
      this.shown = true;
      clearTimeout(this.idleTimer);
      return true;
    }
    close() { if (this.dialog?.open) this.dialog.close(); }
    tryPreview() {
      if (!this.previewPending || this.shown) return;
      if (this.show(true)) this.previewPending = false;
    }
    privacyChanged() {
      if (this.blocked()) this.close();
      if (!this.preview && !this.consentAllowsStorage()) {
        this.close();
        try { localStorage.removeItem(this.key); } catch {}
      }
      this.tryPreview();
      this.armIdle();
    }
    visibilityChanged() {
      const now = performance.now();
      if (document.hidden) { this.hiddenAt = now; clearTimeout(this.idleTimer); return; }
      const away = this.hiddenAt === null ? 0 : now - this.hiddenAt;
      this.hiddenDuration += away;
      this.hiddenAt = null;
      if (!this.preview && away >= 1000 && this.dataset.tab === 'true') this.show();
      this.tryPreview();
      this.armIdle();
    }
    onScroll() {
      this.armIdle();
      const now = performance.now();
      const y = Math.max(0, window.scrollY);
      const delta = this.lastY - y;
      const dt = now - this.lastScroll;
      if (delta > 0 && dt > 0 && dt < 250 && delta / dt >= 0.5) this.upDistance += delta;
      else this.upDistance = 0;
      this.lastY = y;
      this.lastScroll = now;
      if (this.dataset.mobile === 'true' && matchMedia('(pointer: coarse)').matches && this.upDistance >= 120) {
        this.upDistance = 0;
        this.show();
      }
    }
    armIdle() {
      clearTimeout(this.idleTimer);
      if (this.preview || this.shown || this.dataset.idle !== 'true' || document.hidden) return;
      const wait = Math.max((Number(this.dataset.idleSeconds) || 60) * 1000, this.delay - this.elapsed());
      this.idleTimer = setTimeout(() => this.show(), wait);
    }
    position() {
      const bar = document.getElementById('PBarNextFrameWrapper');
      let bottom = 0;
      if (bar) {
        const rect = bar.getBoundingClientRect();
        if (rect.height && rect.top > window.innerHeight / 2 && rect.top < window.innerHeight) bottom = window.innerHeight - rect.top;
        if (!this.barObserver) { this.barObserver = new ResizeObserver(() => this.position()); this.barObserver.observe(bar); }
      }
      this.style.setProperty('--tsro-preview-bottom', bottom + 'px');
    }
    async copy() {
      try {
        await navigator.clipboard.writeText(this.dataset.code);
        this.status.textContent = this.dataset.copySuccess;
      } catch {
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(this.querySelector('.tsro-code'));
          selection.removeAllRanges();
          selection.addRange(range);
        }
        this.status.textContent = this.dataset.copyFallback;
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.barObserver?.disconnect();
      clearTimeout(this.idleTimer);
      clearTimeout(this.previewTimer);
      this.close();
    }
  });
}

